import {
  isHtmlEffectivelyEmpty,
  type EmailFieldErrors,
} from '@kula-ai/components';
import type {
  InterviewCommunicationFormState,
  InterviewVariablesByCategory,
} from './communication.types';
import { SUBJECT_MAX_LENGTH } from './communication.types';

export type InterviewCommunicationValidationErrors = {
  candidateCalendar?: EmailFieldErrors;
  candidateEmail?: EmailFieldErrors;
  interviewerCalendar?: EmailFieldErrors;
  interviewerEmail?: EmailFieldErrors;
  selfSchedule?: EmailFieldErrors;
};

/**
 * Variables context for validating template merge tags in editor content.
 * Each key maps to the variables for a specific communication block.
 */
export type CommunicationValidationVariables = {
  candidateCalendar?: InterviewVariablesByCategory;
  interviewerCalendar?: InterviewVariablesByCategory;
  candidateEmail?: InterviewVariablesByCategory;
  interviewerEmail?: InterviewVariablesByCategory;
  selfSchedule?: InterviewVariablesByCategory;
};

function subjectError(subject: string): string | undefined {
  const t = subject.trim();
  if (!t) {
    return 'Subject is required';
  }
  if (t.length > SUBJECT_MAX_LENGTH) {
    return `Subject must be at most ${SUBJECT_MAX_LENGTH} characters`;
  }
  return undefined;
}

/** Returns true if the content contains `{{…}}` tags that are not in the allowed variables list. */
function hasInvalidVariables(
  content: string,
  variables: InterviewVariablesByCategory | undefined
): boolean {
  if (!variables) {
    return false;
  }
  const allowedNames = Object.values(variables).flat();
  const matches = content.match(/{{\s*(.*?)\s*}}/g);
  if (!matches) {
    return false;
  }
  return matches.some(
    (tag) => !allowedNames.includes(tag.replace(/{{\s*|\s*}}/g, ''))
  );
}

function validateEmailBlock(
  enabled: boolean,
  subject: string,
  content: string,
  from: string,
  variables?: InterviewVariablesByCategory
): EmailFieldErrors | undefined {
  if (!enabled) {
    return undefined;
  }
  const errors: EmailFieldErrors = {};
  const s = subjectError(subject);
  if (s) {
    errors.subject = s;
  }
  if (!from?.trim()) {
    errors.from = 'From is required';
  }
  if (isHtmlEffectivelyEmpty(content)) {
    errors.content = 'Body is required';
  } else if (hasInvalidVariables(content, variables)) {
    errors.content = 'Body contains invalid variable(s)';
  }
  return Object.keys(errors).length ? errors : undefined;
}

function validateCalendarBlock(
  content: string,
  variables?: InterviewVariablesByCategory
): EmailFieldErrors | undefined {
  if (isHtmlEffectivelyEmpty(content)) {
    return { content: 'Body is required' };
  }
  if (hasInvalidVariables(content, variables)) {
    return { content: 'Body contains invalid variable(s)' };
  }
  return undefined;
}

/**
 * Returns field errors for each block; all clear when the form is valid.
 */
export function validateInterviewCommunicationForm(
  state: InterviewCommunicationFormState,
  variables?: CommunicationValidationVariables
): InterviewCommunicationValidationErrors {
  const out: InterviewCommunicationValidationErrors = {};

  const candCal = validateCalendarBlock(
    state.candidateCalendar.content,
    variables?.candidateCalendar
  );
  if (candCal) {
    out.candidateCalendar = candCal;
  }

  const intCal = validateCalendarBlock(
    state.interviewerCalendar.content,
    variables?.interviewerCalendar
  );
  if (intCal) {
    out.interviewerCalendar = intCal;
  }

  const candEmail = validateEmailBlock(
    state.candidateEmailEnabled,
    state.candidateEmail.subject,
    state.candidateEmail.content,
    state.candidateEmail.from,
    variables?.candidateEmail
  );
  if (candEmail) {
    out.candidateEmail = candEmail;
  }

  const intEmail = validateEmailBlock(
    state.interviewerEmailEnabled,
    state.interviewerEmail.subject,
    state.interviewerEmail.content,
    state.interviewerEmail.from,
    variables?.interviewerEmail
  );
  if (intEmail) {
    out.interviewerEmail = intEmail;
  }

  const self = state.selfScheduleEnabled
    ? validateEmailBlock(
        true,
        state.selfSchedule.subject,
        state.selfSchedule.content,
        state.selfSchedule.from,
        variables?.selfSchedule
      )
    : undefined;
  if (self) {
    out.selfSchedule = self;
  }

  return out;
}

export function hasValidationErrors(
  errors: InterviewCommunicationValidationErrors
): boolean {
  return Object.keys(errors).length > 0;
}
